import React, { FC, useEffect, useState, useCallback, useRef } from "react";
import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport, faSearch } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";

// Type definition for PDFDownloadLink render function
import moment from "moment";
import PaginationForm from "../../../../shared/components/molecules/commons/pagination-form";
Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVjJx26TKEr37c9WBI.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/sarabun/v17/DtVmJx26TKEr37c9YL5rulw.ttf",
});
interface PDFDownloadLinkRenderProps {
  blob: Blob | null;
  url: string | null;
  loading: boolean;
  error: Error | null;
}

const zw = "\u200B";
const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const thaiMonthsShort = [
  "ม.ค.",
  "ก.พ.",
  "มี.ค.",
  "เม.ย.",
  "พ.ค.",
  "มิ.ย.",
  "ก.ค.",
  "ส.ค.",
  "ก.ย.",
  "ต.ค.",
  "พ.ย.",
  "ธ.ค.",
];

const ReportRetirementTrendListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
    year: moment().year() + 543,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };
  const [displayYear, setDisplayYear] = useState(moment().year() + 543);

  const headers: Header[] = [
    {
      label: "ลำดับ",
      field: "index",
      order: null,
      isOrder: true,
    },
    {
      label: "ชื่อ-นามสกุล",
      field: "name",
      order: null,
      isOrder: true,
    },
    {
      label: "รหัสพนักงาน",
      field: "code",
      order: null,
      isOrder: true,
    },
    {
      label: "ว/ด/ป เกิด",
      field: "birth_date",
      order: null,
      isOrder: true,
    },
    {
      label: "ตำแหน่ง",
      field: "position",
      order: null,
      isOrder: true,
    },
    {
      label: "ระดับ",
      field: "position_level",
      order: null,
      isOrder: true,
    },
    {
      label: "ว/ด/ป บรรจุ",
      field: "tenure_position_date",
      order: null,
      isOrder: true,
    },
    {
      label: "สังกัด",
      field: "department",
      order: null,
      isOrder: true,
    },
    {
      label: "เงินเดือน",
      field: "salary",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result =
          await HrOnlineService.getReportRetirementTrendList(params);
        const resultAll = await HrOnlineService.getReportRetirementTrendList({
          ...params,
          page_size: 10000,
          page: 1,
        });
        setData(result);
        setDataExport(resultAll);
        setDisplayYear(params.year);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message,
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering, queryParams.page_size, queryParams.page],
  );

  const tableRowNoData = () => {
    if (data.rows?.length === 0) {
      return <TableRowNoData colspan={7} />;
    }
    return null;
  };

  const PDF: React.FC<{ data: any }> = ({ data }) => (
    <Document>
      <Page size="A4" style={{ padding: 20, fontFamily: "kanit-regular" }}>
        {/* หัวรายงาน */}
        <View style={{ marginBottom: 20 }}>
          <Text
            style={{
              fontSize: 14,
              textAlign: "center",
              marginBottom: 1,
            }}
          >
            {"องค์การตลาดเพื่อเกษตรกร" + zw}
          </Text>
          <Text
            style={{
              fontSize: 14,
              textAlign: "center",
              marginBottom: 1,
            }}
          >
            {"รายงานผู้เกษียณอายุในปี " + displayYear + zw}
          </Text>
          <Text
            style={{
              fontSize: 14,
              textAlign: "center",
              marginBottom: 1,
            }}
          >
            {"ประจำวันที่" + zw}{" "}
            {moment().format("D") +
              " " +
              thaiMonths[moment().month()] +
              " " +
              (moment().year() + 543) +
              zw}
          </Text>
        </View>

        {/* หัวตาราง - fixed พร้อมเส้นขอบเหมือน ReportRetirementTrendNext3Years */}
        <View
          fixed
          style={{
            flexDirection: "row",
            border: "1px solid black",
            fontFamily: "kanit-regular",
            fontSize: 8,
            backgroundColor: "white",
          }}
        >
          <Text
            style={{
              width: "7%",
              fontSize: 6,
              textAlign: "center",
              padding: 5,
              borderRight: "1px solid black",
            }}
          >
            {"ลำดับที่" + zw}
          </Text>
          <Text
            style={{
              width: "17%",
              fontSize: 6,
              textAlign: "center",
              padding: 5,
              borderRight: "1px solid black",
            }}
          >
            {"ชื่อ-นามสกุล" + zw}
          </Text>
          <Text
            style={{
              width: "11%",
              fontSize: 6,
              textAlign: "center",
              padding: 5,
              borderRight: "1px solid black",
            }}
          >
            {"รหัสพนักงาน" + zw}
          </Text>
          <Text
            style={{
              width: "11%",
              fontSize: 6,
              textAlign: "center",
              padding: 5,
              borderRight: "1px solid black",
            }}
          >
            {"ว/ด/ป เกิด" + zw}
          </Text>
          <Text
            style={{
              width: "17%",
              fontSize: 6,
              textAlign: "center",
              padding: 5,
              borderRight: "1px solid black",
            }}
          >
            {"ตำแหน่ง" + zw}
          </Text>
          <Text
            style={{
              width: "6%",
              fontSize: 6,
              textAlign: "center",
              padding: 5,
              borderRight: "1px solid black",
            }}
          >
            {"ระดับ" + zw}
          </Text>
          <Text
            style={{
              width: "13%",
              fontSize: 6,
              textAlign: "center",
              padding: 5,
              borderRight: "1px solid black",
            }}
          >
            {"ว/ด/ป บรรจุ" + zw}
          </Text>
          <Text
            style={{
              width: "14%",
              fontSize: 6,
              textAlign: "center",
              padding: 5,
              borderRight: "1px solid black",
            }}
          >
            {"สังกัด" + zw}
          </Text>
          <Text
            style={{
              width: "8%",
              fontSize: 6,
              textAlign: "center",
              padding: 5,
            }}
          >
            {"เงินเดือน" + zw}
          </Text>
        </View>

        {/* ตัวตาราง - ใช้ borderLeft/Right บน container + borderBottom บน row เพื่อไม่ให้เส้นซ้อน */}
        <View
          style={{
            borderLeft: "1px solid black",
            borderRight: "1px solid black",
          }}
        >
          {data.rows?.map((item: any, index: any) => (
            <View
              key={index}
              wrap={false}
              style={{
                flexDirection: "row",
                borderBottom: "1px solid black",
              }}
            >
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "7%",
                  fontSize: 6,
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {index + 1}
              </Text>
              <View
                style={{
                  width: "17%",
                  padding: 5,
                  borderRight: "1px solid black",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    fontSize: 6,
                    textAlign: "left",
                  }}
                >
                  {item.name + zw}
                </Text>
                {item.id_card ? (
                  <Text
                    style={{
                      fontFamily: "kanit-200",
                      fontSize: 6,
                      textAlign: "left",
                      color: "#555555",
                    }}
                  >
                    {item.id_card + zw}
                  </Text>
                ) : null}
              </View>
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "11%",
                  fontSize: 6,
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {item.code + zw}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "11%",
                  fontSize: 6,
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {item.birthday
                  ? moment(item.birthday).format("DD") +
                    " " +
                    thaiMonthsShort[moment(item.birthday).month()] +
                    " " +
                    (moment(item.birthday).year() + 543) +
                    zw
                  : "-"}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "17%",
                  fontSize: 6,
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {item.position_name + zw}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "6%",
                  fontSize: 6,
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {"ระดับ " + item.position_level + zw}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "13%",
                  fontSize: 6,
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {item.tenure_position_date
                  ? (() => {
                      const dateStr =
                        moment(item.tenure_position_date).format("DD") +
                        " " +
                        thaiMonthsShort[
                          moment(item.tenure_position_date).month()
                        ] +
                        " " +
                        (moment(item.tenure_position_date).year() + 543);
                      const d = moment.duration(
                        moment().diff(moment(item.tenure_position_date)),
                      );
                      const years = d.years();
                      const months = d.months();
                      const days = d.days();
                      const parts = [];
                      if (years > 0) parts.push(`${years} ปี`);
                      if (months > 0) parts.push(`${months} เดือน`);
                      if (days > 0) parts.push(`${days} วัน`);
                      const durationStr =
                        parts.length > 0 ? parts.join(" ") : "-";
                      return dateStr + "\n" + durationStr + zw;
                    })()
                  : "-" + zw}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "14%",
                  fontSize: 6,
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {item.division_group_office_name
                  ? item.division_group_office_name + zw
                  : "-" + zw}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "8%",
                  fontSize: 6,
                  textAlign: "right",
                  padding: 5,
                }}
              >
                {item.salary ? item.salary.toLocaleString() : "-" + zw}
              </Text>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQueryParams({
      ...queryParams,
      year: e.target.value,
    });
  };

  const onChangePageSize = (page_size: number) => {
    setQueryParams({
      ...queryParams,
      page_size: page_size,
    });
  };

  const onChangePage = (page: number) => {
    setQueryParams({
      ...queryParams,
      page: page,
    });
  };
  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">
          รายงานผู้เกษียณอายุในปี {displayYear}
        </h2>
      </div>
      {loaderComponent}
      <div className="row">
        <label className="form-label">ปี</label>
        <div className="col-sm-12 col-md-2">
          <input
            className="form-control"
            type="text"
            value={queryParams.year ? queryParams.year : ""}
            onChange={handleYearChange}
            name="search"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Button
            variant="primary"
            type="button"
            className="d-inline-block px-4"
            onClick={() => fetchData(queryParams)}
          >
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </Button>
        </div>
        <div className="col-sm-12 col-md-6 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานผู้เกษียณอายุในปี_${queryParams.year}.pdf`}
          >
            {
              (({ loading }: any) => (
                <Button
                  variant="primary"
                  className="btn-action btn-mof-primary me-2"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  {loading ? "Loading..." : "Export"}
                </Button>
              )) as any
            }
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {data.rows?.map((data: any, index: any) => {
              return (
                <ReportEmployeeCountByTypeRow
                  key={index}
                  {...data}
                  index={index + 1}
                />
              );
            })}
            {tableRowNoData()}
          </tbody>
        </Table>
      </div>
      <div className="">
        <PaginationForm
          current_page={queryParams.page}
          count={data.count}
          page_size={queryParams.page_size}
          onChangePageSize={onChangePageSize}
          onChangePage={onChangePage}
        />
      </div>
    </>
  );
};

const ReportEmployeeCountByTypeRow: FC<any> = ({
  index,
  name,
  id_card,
  code,
  birthday,
  position_name,
  position_level,
  tenure_position_date,
  department_offices_name,
  division_group_office_name,
  structure_departments_name,
  salary,
}) => {
  return (
    <tr>
      <td>{index}</td>
      <td>
        <div>{name}</div>
        {id_card && <div style={{ fontSize: "0.85em" }}>{id_card}</div>}
      </td>
      <td>{code}</td>
      <td>
        {birthday
          ? moment(birthday).format("DD") +
            " " +
            thaiMonthsShort[moment(birthday).month()] +
            " " +
            (moment(birthday).year() + 543)
          : "-"}
      </td>
      <td>{position_name}</td>
      <td>{"ระดับ " + position_level + zw}</td>
      <td>
        {tenure_position_date
          ? moment(tenure_position_date).format("DD") +
            " " +
            thaiMonthsShort[moment(tenure_position_date).month()] +
            " " +
            (moment(tenure_position_date).year() + 543)
          : "-"}{" "}
        <br />
        {tenure_position_date
          ? (() => {
              const d = moment.duration(
                moment().diff(moment(tenure_position_date)),
              );
              const years = d.years();
              const months = d.months();
              const days = d.days();
              const parts = [];
              parts.push(`${years} ปี`);
              parts.push(`${months} เดือน`);
              parts.push(`${days} วัน`);
              return parts.length > 0 ? parts.join(" ") : "-";
            })()
          : "-"}
      </td>
      <td>
        {division_group_office_name ? division_group_office_name : "-" + zw}
      </td>
      <td>{salary ? salary.toLocaleString() : "-"}</td>
    </tr>
  );
};

export default ReportRetirementTrendListPage;
